import { PrimaryNav, TextLink } from '@petsmart-ui/sparky';
import { dayOfWeekAsString, formatToDisplayHours, getClosingTime, getClosingTimeToday, getOpeningTime, getOpeningTimeToday, useStore } from '@libs/common-next';
import type { StandardStoreHours, StoreService } from '@libs/common-node';
import { getStoreDetailsPathname, NAVIGATION_URLS } from '@libs/core';
import { LegacyStars } from './LegacyStars';
import './LegacyMyStore.scss';
export function LegacyMyStore() {
  const {
    selectedStore
  } = useStore();
  const {
    data: store
  } = selectedStore;
  if (!store || !store.TimeZone || !store.PhoneNumber || !store.StoreServices) {
    return null;
  }
  let currentDate: Date;
  let storePath;
  let phoneNumber;
  let isClosedToday;
  let remainingWeekdays: StandardStoreHours[];
  try {
    const timezonedDate = new Date().toLocaleString('en', {
      timeZone: store.TimeZone?.TimeZoneId
    });

    // date we are comparing to uses the timezone of the store we are using
    currentDate = new Date(timezonedDate);
    const currentDay = currentDate.getDay();
    storePath = getStoreDetailsPathname(store);
    const phoneNumbers = store.PhoneNumber.match(/(\d{3})(\d{3})(\d{4})/);
    phoneNumber = store.PhoneNumber;
    if (phoneNumbers) {
      phoneNumber = `(${phoneNumbers[1]}) ${phoneNumbers[2]} - ${phoneNumbers[3]}`;
    }
    const todayStoreHours = (store.StandardStoreHours ?? []).find((h: StandardStoreHours) => h.DayOfWeek === currentDay);
    isClosedToday = todayStoreHours ? todayStoreHours.IsClosed : true;

    // Sort remaining weekdays starting from tomorrow, reusing logic from StoreDetailsHoursModule
    remainingWeekdays = store?.StandardStoreHours?.length ? [...store.StandardStoreHours.slice(currentDay + 1), ...store.StandardStoreHours.slice(0, currentDay)] : [];
  } catch (e) {
    console.error(e);
    return null;
  }
  return <PrimaryNav.Item className="legacy-my-store" data-testid="legacy-my-store">
            <div className="my-store-wrapper">
                <div className="store-details-wrapper">
                    <div className="my-store-details">
                        <p className="my-store-details-title">{store.Name}</p>
                        <LegacyStars starsLength={5} totalReviews="506" href={storePath} rating="4.5" size={{
            height: 16,
            width: 16
          }} />
                        <p className="my-store-details-address">{store.StreetLine1}</p>
                        <p className="my-store-details-address">
                            {store.City}, {store.StateProvinceAbbreviation} {store.ZipPostalCode}
                        </p>
                        <TextLink className="my-store-details-phone" href={`tel:${phoneNumber}`}>
                            {phoneNumber}
                        </TextLink>
                    </div>
                    <div className="store-details-links-wrapper">
                        <a className="store-detail-link store-button-link" aria-label="see store details and events" href={`${storePath}#page_name=global&amp;link_section=header&amp;link_name=our_services`}>
                            See store details
                        </a>
                        <a className="store-directions" href={`https://www.google.com/maps?hl=en&saddr=current+location&daddr=${store.Latitude}%2C${store.Longitude}`} target="_blank" rel="noopener" data-lid="get-directions" data-lpos="my-store" data-link-type="e" data-master="my-store">
                            Get directions
                        </a>
                    </div>
                </div>
                <hr className="my-store-themantic-break" />
                <div className="my-store-main-content-wrapper">
                    <div className="my-store-services">
                        <div className="my-store-services-content">
                            <div className="store-info-header">Store Services</div>
                            {store.StoreServices.map((s: StoreService) => <span key={s.ServiceId}>{s.ServiceName}</span>)}
                        </div>
                    </div>
                    <div className="my-store-hours">
                        <div className="my-store-hours-content">
                            <div className="store-info-header">Store hours</div>
                            <div className="day-wrapper">
                                <span className="store-days">Today</span>
                                <span className="store-times">
                                    {isClosedToday ? 'Closed' : `${formatToDisplayHours(getOpeningTimeToday(store, currentDate))}-
                                    ${formatToDisplayHours(getClosingTimeToday(store, currentDate))}`}
                                </span>
                            </div>
                            {remainingWeekdays.map((h_0: StandardStoreHours) => {
              return <div key={h_0.DayOfWeek} className="day-wrapper">
                                        <span className="store-days">{dayOfWeekAsString(h_0.DayOfWeek)}</span>
                                        <span className="store-times">
                                            {h_0.IsClosed ? 'Closed' : `${formatToDisplayHours(getOpeningTime(store, currentDate, h_0.DayOfWeek))}-
                                            ${formatToDisplayHours(getClosingTime(store, currentDate, h_0.DayOfWeek))}`}
                                        </span>
                                    </div>;
            })}
                        </div>
                        <div className="store-hours-disclaimer">
                            <p>
                                <b>Note:</b> Hours for Services (Grooming, PetsHotel and Training) and Holidays may
                                vary. Please see{' '}
                                <a href={`${storePath}#page_name=global&amp;link_section=header&amp;link_name=our_services`}>
                                    store details
                                </a>{' '}
                                or contact the store for more information.
                            </p>
                        </div>
                    </div>
                </div>
                <hr className="my-store-themantic-break" />
                <div className="more-stores-wrapper">
                    <a className="more-stores-link store-button-link" data-lid="find-more-stores" data-lpos="my-store" data-link-type="o" data-master="my-store" href={`${NAVIGATION_URLS.legacy.storeLocator.base}#page_name=global&amp;link_section=header&amp;link_name=find_more_stores`}>
                        Find another store
                    </a>
                </div>
            </div>
        </PrimaryNav.Item>;
}